import type { DisplayJob } from '../../types';
import type { JobFormState } from '../../redux/features/jobFormSlice';
import type { SearchInferenceBullet } from '../../types/job-match';
import { ROLE_LEVEL_HIERARCHY, normalizeJobSalaryToMonthly, type MatchScoreBreakdown } from '../data/calculate-job-match-score';

/**
 * The job page's "fit ledger": the match score turned back into the evidence behind it, one row
 * per scored dimension, with the user's own value on one side and the listing's on the other.
 *
 * Everything here is pure and locale-free: cells carry raw values (numbers, facet strings) and
 * deltas are discriminated unions, so the UI does all wording and number formatting through i18n.
 * The per-row scores are read straight from the breakdown calculateJobMatchScore already produced
 * (never recomputed), so the ledger can never disagree with the donut at the top of the page.
 */

export type FitRowKey =
  | 'title'
  | 'roleLevel'
  | 'location'
  | 'remoteness'
  | 'jobType'
  | 'salary'
  | 'languages'
  | 'vacation'
  | 'benefits';

/** Which profile section a row's "you" side came from; shown in the ledger's footnote and pay wording. */
export type FitBasis = 'desired' | 'current';

export type FitCell =
  | { kind: 'empty' }
  | { kind: 'text'; text: string }
  | { kind: 'list'; items: string[]; more: number }
  | { kind: 'money'; min: number; max: number }
  | { kind: 'weeks'; weeks: number }
  | { kind: 'count'; count: number }
  | { kind: 'chips'; chips: Array<{ label: string; state: 'have' | 'missing' | 'gain' | 'plain' }>; more: number };

export type FitDelta =
  | { kind: 'none' }
  | { kind: 'notStated' }
  | { kind: 'salaryOver'; amount: number; basis: FitBasis }
  | { kind: 'salaryMeets'; basis: FitBasis }
  | { kind: 'salaryRangeCovers'; basis: FitBasis }
  | { kind: 'salaryUnder'; amount: number; basis: FitBasis }
  | { kind: 'levelSame' }
  | { kind: 'levelUp'; steps: number }
  | { kind: 'levelDown'; steps: number }
  | { kind: 'levelDifferent' }
  | { kind: 'titleExact' }
  | { kind: 'titleClose' }
  | { kind: 'titleOverlap' }
  | { kind: 'titleDifferent' }
  | { kind: 'locationInYourArea' }
  | { kind: 'locationOutside' }
  | { kind: 'styleExact' }
  | { kind: 'styleHybridCompromise' }
  | { kind: 'styleMismatch' }
  | { kind: 'contractSame' }
  | { kind: 'contractDifferent' }
  | { kind: 'languagesAllCovered'; count: number }
  | { kind: 'languagesMissing'; missing: string[] }
  | { kind: 'languagesNoneRequired' }
  | { kind: 'vacationMore'; weeks: number }
  | { kind: 'vacationSame' }
  | { kind: 'vacationLess'; weeks: number }
  | { kind: 'benefitsGain'; count: number }
  | { kind: 'benefitsSame' }
  | { kind: 'benefitsFewer'; count: number };

export interface FitLedgerRow {
  key: FitRowKey;
  /** 0-100, straight from the match breakdown. */
  score: number;
  /** The user's own weight for this dimension; rows are ordered by it so the top row is what they said matters most. */
  weight: number;
  basis: FitBasis;
  you: FitCell;
  job: FitCell;
  delta: FitDelta;
}

export interface FitLedger {
  rows: FitLedgerRow[];
  /** Row keys scoring >= 70, in row order. */
  strengths: FitRowKey[];
  /** Row keys scoring < 40, in row order. */
  gaps: FitRowKey[];
  /** Required languages the profile doesn't list; drives the "add it to your profile" action. */
  missingLanguages: string[];
  /** True when at least one row is based on the desired-job section rather than the current role. */
  usesDesiredJob: boolean;
}

export const FIT_STRENGTH_THRESHOLD = 70;
export const FIT_GAP_THRESHOLD = 40;

/** Tie-break order when two rows share a weight; roughly "what a recruiter would ask about first". */
const NATURAL_ORDER: FitRowKey[] = ['title', 'salary', 'location', 'remoteness', 'roleLevel', 'jobType', 'languages', 'vacation', 'benefits'];

const LIST_PREVIEW = 3;

function clean(values: unknown): string[] {
  if (!Array.isArray(values)) return [];
  return values.map((v) => String(v ?? '').trim()).filter(Boolean);
}

function norm(value: string): string {
  return value.toLowerCase().trim();
}

function listCell(items: string[]): FitCell {
  if (items.length === 0) return { kind: 'empty' };
  return { kind: 'list', items: items.slice(0, LIST_PREVIEW), more: Math.max(0, items.length - LIST_PREVIEW) };
}

function textCell(text: string | undefined): FitCell {
  const t = (text ?? '').trim();
  return t ? { kind: 'text', text: t } : { kind: 'empty' };
}

function weightFor(weights: MatchScoreBreakdown['weights'], key: FitRowKey): number {
  const w = weights as Record<string, number | undefined>;
  switch (key) {
    case 'title': return w.titleWeight ?? 0;
    case 'roleLevel': return w.roleLevelWeight ?? 0;
    case 'location': return w.locationWeight ?? 0;
    case 'remoteness': return w.remotenessWeight ?? 0;
    case 'jobType': return w.jobTypeWeight ?? 0;
    case 'salary': return w.salaryWeight ?? 0;
    case 'languages': return w.languagesWeight ?? 0;
    case 'vacation': return w.vacationWeight ?? 0;
    case 'benefits': return w.benefitsWeight ?? 0;
    default: return 0;
  }
}

export interface BuildFitLedgerArgs {
  job: DisplayJob;
  profile: JobFormState;
  breakdown: MatchScoreBreakdown;
  /**
   * The listing's pay already converted to monthly EUR by the page (the same figures its salary
   * label shows), so the ledger's "over/under your floor" delta agrees with the number printed
   * next to it. Falls back to a period-only normalisation of the raw range when absent.
   */
  jobSalaryMonthly?: { min: number; max: number } | null;
}

export function buildFitLedger({ job, profile, breakdown, jobSalaryMonthly }: BuildFitLedgerArgs): FitLedger {
  const current = profile.currentJob;
  const desired = profile.desiredJob;
  const rows: FitLedgerRow[] = [];
  let missingLanguages: string[] = [];

  // Title
  if (typeof breakdown.title === 'number') {
    const desiredTitles = clean(desired?.titles);
    const basis: FitBasis = desiredTitles.length > 0 ? 'desired' : 'current';
    const youTitles = basis === 'desired' ? desiredTitles : clean([current?.title || current?.normalisedTitle]);
    const s = breakdown.title;
    const delta: FitDelta =
      s >= 100 ? { kind: 'titleExact' } : s >= 60 ? { kind: 'titleClose' } : s >= 40 ? { kind: 'titleOverlap' } : { kind: 'titleDifferent' };
    rows.push({
      key: 'title',
      score: s,
      weight: weightFor(breakdown.weights, 'title'),
      basis,
      you: listCell(youTitles),
      // The title the reader just saw in the H1, not the normalised one the score was computed against.
      job: textCell(job.title || job.normalisedTitle),
      delta,
    });
  }

  // Role level
  if (typeof breakdown.roleLevel === 'number') {
    const desiredLevels = clean(desired?.normalisedRoleLevels);
    const basis: FitBasis = desiredLevels.length > 0 ? 'desired' : 'current';
    const youLevels = basis === 'desired' ? desiredLevels : clean([current?.normalisedRoleLevel]);
    const jobLevel = norm(job.normalisedRoleLevel ?? '');
    const jobRank = ROLE_LEVEL_HIERARCHY[jobLevel] ?? 0;
    let delta: FitDelta = { kind: 'levelDifferent' };
    if (youLevels.some((l) => norm(l) === jobLevel)) {
      delta = { kind: 'levelSame' };
    } else if (jobRank > 0) {
      // Closest of the user's levels decides the direction, matching scoreRoleLevel's "best" pick.
      const ranked = youLevels.map((l) => ROLE_LEVEL_HIERARCHY[norm(l)] ?? 0).filter((r) => r > 0);
      if (ranked.length > 0) {
        const closest = ranked.reduce((best, r) => (Math.abs(r - jobRank) < Math.abs(best - jobRank) ? r : best), ranked[0]);
        const steps = jobRank - closest;
        delta = steps === 0 ? { kind: 'levelSame' } : steps > 0 ? { kind: 'levelUp', steps } : { kind: 'levelDown', steps: -steps };
      }
    }
    rows.push({
      key: 'roleLevel',
      score: breakdown.roleLevel,
      weight: weightFor(breakdown.weights, 'roleLevel'),
      basis,
      you: listCell(youLevels),
      job: textCell(job.normalisedRoleLevel),
      delta,
    });
  }

  // Location
  if (typeof breakdown.location === 'number') {
    const desiredLocations = clean(desired?.locations);
    const basis: FitBasis = desiredLocations.length > 0 ? 'desired' : 'current';
    const youLocations = basis === 'desired' ? desiredLocations : clean(current?.locations);
    rows.push({
      key: 'location',
      score: breakdown.location,
      weight: weightFor(breakdown.weights, 'location'),
      basis,
      you: listCell(youLocations),
      job: listCell(clean(job.location)),
      delta: breakdown.location > 0 ? { kind: 'locationInYourArea' } : { kind: 'locationOutside' },
    });
  }

  // Working style (remoteness)
  {
    const desiredRemoteness = clean(desired?.remoteness);
    const basis: FitBasis = desiredRemoteness.length > 0 ? 'desired' : 'current';
    const youRemoteness = basis === 'desired' ? desiredRemoteness : clean([current?.remoteness]);
    const jobRemoteness = (job.remoteness ?? '').trim();
    if (youRemoteness.length > 0 && jobRemoteness) {
      const s = breakdown.remoteness;
      rows.push({
        key: 'remoteness',
        score: s,
        weight: weightFor(breakdown.weights, 'remoteness'),
        basis,
        you: listCell(youRemoteness),
        job: textCell(jobRemoteness),
        delta: s >= 100 ? { kind: 'styleExact' } : s >= 60 ? { kind: 'styleHybridCompromise' } : { kind: 'styleMismatch' },
      });
    }
  }

  // Contract type
  if (typeof breakdown.jobType === 'number') {
    const desiredTypes = clean(desired?.jobTypes);
    const basis: FitBasis = desiredTypes.length > 0 ? 'desired' : 'current';
    const youTypes = basis === 'desired' ? desiredTypes : clean([current?.jobType]);
    rows.push({
      key: 'jobType',
      score: breakdown.jobType,
      weight: weightFor(breakdown.weights, 'jobType'),
      basis,
      you: listCell(youTypes),
      job: textCell(job.jobType),
      delta: breakdown.jobType >= 100 ? { kind: 'contractSame' } : { kind: 'contractDifferent' },
    });
  }

  // Pay (all monthly; see calculateJobMatchScore for the same unit handling)
  {
    const desiredMin = typeof desired?.salaryMin === 'number' && desired.salaryMin > 0 ? desired.salaryMin : 0;
    const currentMonthly = typeof current?.salary === 'number' && current.salary > 0 ? current.salary : 0;
    const basis: FitBasis = desiredMin > 0 ? 'desired' : 'current';
    const floor = desiredMin > 0 ? desiredMin : currentMonthly;
    if (floor > 0) {
      const rawMin = typeof job.salaryRangeMin === 'number' && job.salaryRangeMin > 0 ? job.salaryRangeMin : 0;
      const rawMax = typeof job.salaryRangeMax === 'number' && job.salaryRangeMax > 0 ? job.salaryRangeMax : rawMin;
      const jobMin = jobSalaryMonthly?.min ?? (rawMin > 0 ? normalizeJobSalaryToMonthly(rawMin, job.salaryPeriod) : 0);
      const jobMax = jobSalaryMonthly?.max ?? (rawMax > 0 ? normalizeJobSalaryToMonthly(rawMax, job.salaryPeriod) : jobMin);
      let delta: FitDelta = { kind: 'notStated' };
      let jobCell: FitCell = { kind: 'empty' };
      if (jobMin > 0) {
        jobCell = { kind: 'money', min: Math.round(jobMin), max: Math.round(Math.max(jobMin, jobMax)) };
        const over = Math.round(jobMin - floor);
        if (over >= 50) delta = { kind: 'salaryOver', amount: over, basis };
        else if (over >= 0) delta = { kind: 'salaryMeets', basis };
        else if (jobMax >= floor) delta = { kind: 'salaryRangeCovers', basis };
        else delta = { kind: 'salaryUnder', amount: Math.round(floor - jobMax), basis };
      }
      rows.push({
        key: 'salary',
        score: breakdown.salary,
        weight: weightFor(breakdown.weights, 'salary'),
        basis,
        you: { kind: 'money', min: Math.round(floor), max: Math.round(floor) },
        job: jobCell,
        delta,
      });
    }
  }

  // Languages
  {
    const youLangs = clean(current?.languages);
    const required = clean(job.requiredLanguages);
    if (youLangs.length > 0 || required.length > 0) {
      const have = new Set(youLangs.map(norm));
      const missing = required.filter((r) => !have.has(norm(r)));
      missingLanguages = missing;
      const chips = required.map((r) => ({ label: r, state: have.has(norm(r)) ? ('have' as const) : ('missing' as const) }));
      const delta: FitDelta =
        required.length === 0
          ? { kind: 'languagesNoneRequired' }
          : missing.length === 0
            ? { kind: 'languagesAllCovered', count: required.length }
            : { kind: 'languagesMissing', missing };
      rows.push({
        key: 'languages',
        score: breakdown.languages,
        weight: weightFor(breakdown.weights, 'languages'),
        basis: 'current',
        you: listCell(youLangs),
        job: chips.length > 0 ? { kind: 'chips', chips, more: 0 } : { kind: 'empty' },
        delta,
      });
    }
  }

  // Time off
  {
    const youWeeks = typeof current?.vacationWeeks === 'number' && current.vacationWeeks > 0 ? current.vacationWeeks : 0;
    if (youWeeks > 0) {
      const jobWeeks = typeof job.vacationWeeks === 'number' && job.vacationWeeks > 0 ? job.vacationWeeks : 0;
      const diff = jobWeeks - youWeeks;
      const delta: FitDelta =
        jobWeeks <= 0
          ? { kind: 'notStated' }
          : diff > 0
            ? { kind: 'vacationMore', weeks: diff }
            : diff === 0
              ? { kind: 'vacationSame' }
              : { kind: 'vacationLess', weeks: -diff };
      rows.push({
        key: 'vacation',
        score: breakdown.vacation,
        weight: weightFor(breakdown.weights, 'vacation'),
        basis: 'current',
        you: { kind: 'weeks', weeks: youWeeks },
        job: jobWeeks > 0 ? { kind: 'weeks', weeks: jobWeeks } : { kind: 'empty' },
        delta,
      });
    }
  }

  // Benefits
  {
    const youBenefits = clean(current?.benefits);
    if (youBenefits.length > 0) {
      const jobBenefits = clean(job.benefits);
      const have = new Set(youBenefits.map(norm));
      const gains = jobBenefits.filter((b) => !have.has(norm(b)));
      const chips = [
        ...gains.map((b) => ({ label: b, state: 'gain' as const })),
        ...jobBenefits.filter((b) => have.has(norm(b))).map((b) => ({ label: b, state: 'plain' as const })),
      ];
      const delta: FitDelta =
        jobBenefits.length === 0
          ? { kind: 'notStated' }
          : gains.length > 0
            ? { kind: 'benefitsGain', count: gains.length }
            : jobBenefits.length >= youBenefits.length
              ? { kind: 'benefitsSame' }
              : { kind: 'benefitsFewer', count: youBenefits.length - jobBenefits.length };
      rows.push({
        key: 'benefits',
        score: breakdown.benefits,
        weight: weightFor(breakdown.weights, 'benefits'),
        basis: 'current',
        you: { kind: 'count', count: youBenefits.length },
        job: chips.length > 0 ? { kind: 'chips', chips: chips.slice(0, LIST_PREVIEW), more: Math.max(0, chips.length - LIST_PREVIEW) } : { kind: 'empty' },
        delta,
      });
    }
  }

  rows.sort((a, b) => b.weight - a.weight || NATURAL_ORDER.indexOf(a.key) - NATURAL_ORDER.indexOf(b.key));

  return {
    rows,
    strengths: rows.filter((r) => r.score >= FIT_STRENGTH_THRESHOLD).map((r) => r.key),
    // A required language the profile lacks is a hard blocker in practice, so it counts as a gap
    // even when the row's score sits above the threshold (e.g. one of two languages covered = 50).
    gaps: rows.filter((r) => r.score < FIT_GAP_THRESHOLD || r.delta.kind === 'languagesMissing').map((r) => r.key),
    missingLanguages,
    usesDesiredJob: rows.some((r) => r.basis === 'desired'),
  };
}

/**
 * The search-inferred variant (no profile yet; score estimated from recent searches). The
 * breakdown's five slots are reused by inferJobMatchFromRecentSearches with different meanings
 * (salary→keywords, vacation→location, benefits→role & field, remoteness→remoteness,
 * languages→employer), so this maps each bullet back to the slot it was scored in.
 */
export type SearchFitRowKey = 'keywords' | 'searchLocation' | 'searchRemoteness' | 'searchRoles' | 'searchCompany';

export interface SearchFitRow {
  key: SearchFitRowKey;
  score: number;
  you: FitCell;
  job: FitCell;
}

export function buildSearchFitRows(job: DisplayJob, breakdown: MatchScoreBreakdown, bullets: SearchInferenceBullet[]): SearchFitRow[] {
  const rows: SearchFitRow[] = [];
  for (const bullet of bullets) {
    switch (bullet.type) {
      case 'keywords':
        rows.push({ key: 'keywords', score: breakdown.salary, you: textCell(bullet.terms), job: textCell(job.normalisedTitle || job.title) });
        break;
      case 'locations':
        rows.push({ key: 'searchLocation', score: breakdown.vacation, you: textCell(bullet.locations), job: listCell(clean(job.location)) });
        break;
      case 'remoteness':
        rows.push({ key: 'searchRemoteness', score: breakdown.remoteness, you: textCell(bullet.values), job: textCell(job.remoteness) });
        break;
      case 'roles':
        rows.push({
          key: 'searchRoles',
          score: breakdown.benefits,
          you: textCell(bullet.hints),
          job: listCell(clean([job.normalisedRoleLevel, job.jobType, job.field])),
        });
        break;
      case 'company':
        rows.push({ key: 'searchCompany', score: breakdown.languages, you: textCell(bullet.company), job: textCell(job.companyName || job.companyId) });
        break;
      default:
        break;
    }
  }
  return rows;
}
